import { getIntlLocale } from 'Helpers/localeService';

/* Every timezone-aware read the app makes goes through here.
 *
 * Two rules hold throughout, and both exist because breaking either one
 * produces a wrong answer that looks right on the developer's machine:
 *
 *  - EVERY Intl call is given BOTH the locale and the timeZone explicitly. A
 *    bare toLocaleString() reads the browser's, which is the traveller's laptop
 *    and not the zone the user configured — so a Sydney user in a London hotel
 *    would be told their event's dates in GMT.
 *  - "Today" is the user's calendar day, resolved in their zone. Anything that
 *    decides overdue / due today / due tomorrow compares calendar dates, never
 *    timestamps, so the flip happens at the user's midnight rather than UTC's.
 *
 * Due dates arriving from the API are plain calendar dates (YYYY-MM-DD) with no
 * time and no zone. They are compared as written and never pushed through a
 * timezone conversion, which would shift them a day either way.
 *
 * A third rule follows from the first: A NUMBER THAT WILL BE PARSED IS NEVER
 * TAKEN FROM A DISPLAY LOCALE. `ar-EG` and `ar-SA` default to the `arab`
 * numbering system, so an hour formatted in the user's locale comes back as
 * "١٥" and parseInt gives NaN. Anything read back as a number goes through
 * partsInZone() below, which is pinned to a fixed locale; the user's locale is
 * for text they will read.
 *
 * Some exports here have no caller yet — getUserTimezone, startOfUserDay,
 * getUserToday, isDueToday, isDueTomorrow and isOverdue. They are staged, not
 * abandoned: this module is the single home for timezone-aware reads, and the
 * work-section tickets wire their overdue and due-today calculations into these
 * rather than growing their own. Keep them working and keep them tested.
 */

const FALLBACK_TIMEZONE = 'UTC';

/* ISO ordering (YYYY-MM-DD) comes out of en-CA, which is why it is hardcoded
   here: these strings are compared, never shown. */
const ISO_DATE_LOCALE = 'en-CA';

/**
 * The IANA zone the user's dates are read in — published by the layout as
 * USER_TIMEZONE. The browser's own zone stands in only where the global is
 * missing (a view that predates it), and UTC where even that is unavailable.
 */
export const getUserTimezone = () => {
    if (typeof window.USER_TIMEZONE === 'string' && window.USER_TIMEZONE !== '') {
        return window.USER_TIMEZONE;
    }

    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_TIMEZONE;
};

const formatter = (options) => new Intl.DateTimeFormat(getIntlLocale(), { timeZone: getUserTimezone(), ...options });

/**
 * The parts of an instant, read in a zone against the fixed ISO locale.
 *
 * Every numeric read in this module goes through here. The locale is pinned so
 * the digits come back as ASCII whatever the user's own locale would have
 * rendered them in.
 */
const partsInZone = (instant, options, timeZone = getUserTimezone()) =>
    new Intl.DateTimeFormat(ISO_DATE_LOCALE, { timeZone, ...options })
        .formatToParts(instant)
        .reduce((carry, part) => ({ ...carry, [part.type]: part.value }), {});

const CALENDAR_PARTS = { year: 'numeric', month: '2-digit', day: '2-digit' };

/** A calendar date as YYYY-MM-DD, read in the given zone. */
const isoDateIn = (date, timeZone) => {
    const parts = partsInZone(date, CALENDAR_PARTS, timeZone);

    return `${parts.year}-${parts.month}-${parts.day}`;
};

/** The user's calendar date for an instant, as YYYY-MM-DD. */
const isoDateInZone = (date) => isoDateIn(date, getUserTimezone());

/** Time of day, e.g. "1:59 pm" — 12- or 24-hour by the user's locale, not by ours. */
export const formatTime = (date = new Date()) => formatter({ hour: 'numeric', minute: '2-digit' }).format(date);

/** A moment, e.g. "12 Aug 2026, 1:59 pm", in the user's zone and locale. */
export const formatDateTime = (date = new Date()) =>
    formatter({ day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date);

/** The full date, e.g. "Wednesday 12 August 2026". */
export const formatFullDate = (date = new Date()) => formatter({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(date);

/**
 * The hour of the user's day, 0–23 — what a greeting keys off.
 *
 * Read against the fixed ISO locale, never the user's. An Arabic regional
 * locale renders the hour in Arabic-Indic digits, which parse to NaN — and NaN
 * fails every `<` comparison silently, so the greeting would settle on whatever
 * the last branch happens to be rather than throwing.
 */
export const getLocalHour = (date = new Date()) => Number(partsInZone(date, { hour: '2-digit', hourCycle: 'h23' }).hour);

/**
 * The offset of the user's zone, in milliseconds, at a given instant. Derived
 * from the formatted wall-clock reading so it follows DST rather than assuming
 * the zone's standard offset.
 */
const zoneOffsetMs = (instant) => {
    const parts = partsInZone(instant, {
        ...CALENDAR_PARTS,
        hourCycle: 'h23',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });

    const wallClock = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));

    return wallClock - Math.floor(instant.getTime() / 1000) * 1000;
};

/**
 * The instant midnight last struck in the user's zone.
 *
 * The offset is probed twice because a DST transition can sit between the first
 * guess and the true midnight, in which case the first offset is the wrong side
 * of the change and lands an hour out.
 */
export const startOfUserDay = (date = new Date()) => {
    const [year, month, day] = isoDateInZone(date).split('-').map(Number);
    const utcMidnight = Date.UTC(year, month - 1, day);
    const firstGuess = utcMidnight - zoneOffsetMs(new Date(utcMidnight));

    return new Date(utcMidnight - zoneOffsetMs(new Date(firstGuess)));
};

/** The user's calendar date today, as YYYY-MM-DD. */
export const getUserToday = () => isoDateInZone(new Date());

/**
 * A calendar date shifted by whole days, staying a calendar date throughout.
 *
 * Built at midday UTC and read back in UTC. Reading it back in the USER'S zone
 * would re-apply their offset to a value that never had one: east of UTC+12 the
 * midday-UTC instant is already the following day locally, so "tomorrow" came
 * back as the day after. Midday is the anchor because it survives any offset
 * within ±12h without crossing a date boundary.
 */
const shiftDays = (isoDate, days) => {
    const [year, month, day] = isoDate.split('-').map(Number);

    return isoDateIn(new Date(Date.UTC(year, month - 1, day + days, 12)), 'UTC');
};

/* A due date may arrive as 'YYYY-MM-DD' or as a full ISO timestamp; only the
   calendar part of either is ever compared. */
const dueDateKey = (due) => (typeof due === 'string' ? due.slice(0, 10) : isoDateInZone(due));

export const isDueToday = (due) => !!due && dueDateKey(due) === getUserToday();

export const isDueTomorrow = (due) => !!due && dueDateKey(due) === shiftDays(getUserToday(), 1);

export const isOverdue = (due) => !!due && dueDateKey(due) < getUserToday();

/**
 * A calendar date with its YEAR, e.g. "15 Sep 2025".
 *
 * What every date cell in a Home list renders. `formatShortDate` below drops the
 * year and leads with the weekday, which reads well for something due this week
 * and badly for a contract ending in two years' time — a table holding both
 * cannot tell them apart without it.
 *
 * Built and formatted in UTC for the same reason formatShortDate is: the value
 * is a calendar date with no zone of its own.
 */
export const formatDateWithYear = (value) => {
    if (!value) {
        return '';
    }

    const [year, month, day] = dueDateKey(value).split('-').map(Number);

    return new Intl.DateTimeFormat(getIntlLocale(), { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' }).format(
        new Date(Date.UTC(year, month - 1, day, 12)),
    );
};

/** A due date rendered the short way a tile shows it, e.g. "Fri 14 Aug". */
export const formatShortDate = (due) => {
    if (!due) {
        return '';
    }

    const [year, month, day] = dueDateKey(due).split('-').map(Number);

    /* Built at midday UTC and formatted in UTC: the value is a calendar date
       with no zone of its own, so pushing it through the user's zone could
       shift it onto the day before. */
    return new Intl.DateTimeFormat(getIntlLocale(), { timeZone: 'UTC', weekday: 'short', day: 'numeric', month: 'short' }).format(
        new Date(Date.UTC(year, month - 1, day, 12)),
    );
};
